import {
  AspectRatioVariant,
  BorderRadius,
  Button,
  FilesPreview,
  FileType,
  getFileThumbnail,
  PreviewFile,
  SkeletonBox,
  Spacing,
} from '@billy/design-system'
import download from 'downloadjs'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getImageClassName } from '../../../../utils/getClassName'
import { useFetchImportedInvoiceFile } from '../../hooks/useFetchImportedInvoiceFile'
import { ImportedInvoiceDocumentPreviewMessage } from './elements/ImportedInvoiceDocumentPreviewMessage'
import * as Styled from './styles'

interface ImportedInvoiceDocumentPreviewProps {
  fileId?: string
}

export const ImportedInvoiceDocumentPreview = ({ fileId }: ImportedInvoiceDocumentPreviewProps): ReactElement => {
  const [previewErrorFileId, setPreviewErrorFileId] = useState<string>()
  const { t } = useTranslation()
  const { file, isError, isLoading, refetch } = useFetchImportedInvoiceFile(fileId)
  const downloadUrl = file?.downloadUrl
  const hasPreviewError = previewErrorFileId === fileId
  // A file without a download URL has nothing to render, which reads the same as a missing one.
  const isUnavailable = !fileId || !downloadUrl

  const previewFiles: PreviewFile[] = useMemo(() => {
    if (!file?.downloadUrl) {
      return []
    }

    return [
      {
        id: file.id,
        src: file.isPdf ? file.downloadUrl : getFileThumbnail(file, 'medium'),
        srcDownload: file.downloadUrl,
        srcZoom: file.isPdf ? file.downloadUrl : getFileThumbnail(file, 'large'),
      },
    ]
  }, [file])

  const handlePreviewError = useCallback(() => {
    setPreviewErrorFileId(fileId)
  }, [fileId])

  const handleRetryButtonClick = useCallback(() => {
    refetch()
  }, [refetch])

  const handleDownloadButtonClick = useCallback(() => {
    if (downloadUrl) {
      download(downloadUrl)
    }
  }, [downloadUrl])

  if (isLoading) {
    return (
      <Styled.ImportedInvoiceDocumentPreviewWrapper
        aria-label={t('imported_invoices.detail.document.loading')}
        role="status"
      >
        <SkeletonBox aspectRatio={AspectRatioVariant.billAttachment} />
      </Styled.ImportedInvoiceDocumentPreviewWrapper>
    )
  }

  if (isError) {
    return (
      <ImportedInvoiceDocumentPreviewMessage message={t('review_entries.detail.document.load_error')}>
        <Button onClick={handleRetryButtonClick} size="md" variant="outlined">
          {t('imported_invoices.detail.document.retry')}
        </Button>
      </ImportedInvoiceDocumentPreviewMessage>
    )
  }

  if (isUnavailable) {
    return <ImportedInvoiceDocumentPreviewMessage message={t('review_entries.detail.document.unavailable')} />
  }

  if (hasPreviewError) {
    return (
      <ImportedInvoiceDocumentPreviewMessage message={t('imported_invoices.detail.document.preview_error')}>
        <Button onClick={handleDownloadButtonClick} size="md" variant="outlined">
          {t('download')}
        </Button>
      </ImportedInvoiceDocumentPreviewMessage>
    )
  }

  return (
    <Styled.ImportedInvoiceDocumentPreviewWrapper>
      <FilesPreview
        aspectRatio={AspectRatioVariant.billAttachment}
        asType={file?.isPdf ? FileType.Pdf : FileType.Image}
        borderRadius={BorderRadius.R8}
        className={getImageClassName()}
        files={previewFiles}
        fitToHeight
        isFloatingActionsView
        margin={Spacing.S16}
        onError={handlePreviewError}
        withDownloadButton
        withRotate
        withShadow
        zoomable
      />
    </Styled.ImportedInvoiceDocumentPreviewWrapper>
  )
}
